import React, { useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Send, SmilePlus } from "lucide-react";

const REACT_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "✅"];
const timeShort = (iso) => { try { return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }); } catch { return ""; } };
const firstName = (p) => ((p?.name || p?.email || "").split(" ")[0] || "");

// Reusable message thread for one channel/DM. Loads its own data, polls every 5s while mounted,
// renders iMessage-style bubbles, supports @mentions + read receipts. Used by the Messages page
// and the per-project Team Chat slide-over.
export default function ChatThread({ channelId, onActivity }) {
  const { user } = useAuth();
  const me = user?.user_id;
  const [msgs, setMsgs] = useState([]);
  const [members, setMembers] = useState([]);
  const [reads, setReads] = useState({});
  const [people, setPeople] = useState([]);
  const [text, setText] = useState("");
  const [mentionIds, setMentionIds] = useState(() => new Set());
  const [atOpen, setAtOpen] = useState(false);
  const [atQuery, setAtQuery] = useState("");
  const [pickerFor, setPickerFor] = useState(null);
  const scrollRef = useRef(null);
  const taRef = useRef(null);

  const scrollToEnd = () => requestAnimationFrame(() => { const el = scrollRef.current; if (el) el.scrollTop = el.scrollHeight; });

  const load = useCallback(async (markRead = true) => {
    if (!channelId) return;
    try {
      const { data } = await api.get(`/messaging/channels/${channelId}/messages`);
      setMsgs(data.messages || []);
      setMembers(data.members || []);
      setReads(data.reads || {});
      if (markRead) api.post(`/messaging/channels/${channelId}/read`).catch(() => {});
    } catch { /* ignore */ }
  }, [channelId]);

  useEffect(() => { setMsgs([]); load(); }, [load]);
  useEffect(() => { api.get("/messaging/people").then((r) => setPeople(r.data || [])).catch(() => {}); }, []);
  useEffect(() => { scrollToEnd(); }, [msgs.length]);
  useEffect(() => {
    if (!channelId) return;
    const t = setInterval(() => load(true), 5000);
    return () => clearInterval(t);
  }, [channelId, load]);

  const others = members.filter((m) => m.user_id !== me);
  const seenBy = (m) => others.filter((o) => (reads[o.user_id] || "") >= m.created_at);
  let lastMineIdx = -1;
  msgs.forEach((m, i) => { if (m.author_id === me) lastMineIdx = i; });

  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const caret = e.target.selectionStart ?? v.length;
    const upto = v.slice(0, caret);
    const at = upto.lastIndexOf("@");
    if (at >= 0 && (at === 0 || /\s/.test(upto[at - 1]))) {
      const q = upto.slice(at + 1);
      if (!/\s/.test(q)) { setAtQuery(q); setAtOpen(true); return; }
    }
    setAtOpen(false);
  };
  const pickMention = (p) => {
    const v = text;
    const caret = taRef.current?.selectionStart ?? v.length;
    const upto = v.slice(0, caret);
    const at = upto.lastIndexOf("@");
    const nv = v.slice(0, at) + `@${p.name || p.email} ` + v.slice(caret);
    setText(nv);
    setMentionIds((s) => new Set([...s, p.user_id]));
    setAtOpen(false);
    taRef.current?.focus();
  };

  const send = async () => {
    const t = text.trim();
    if (!t || !channelId) return;
    const ids = [...mentionIds].filter((id) => {
      const p = people.find((x) => x.user_id === id);
      return p && t.includes(`@${p.name || p.email}`);
    });
    setText(""); setMentionIds(new Set()); setAtOpen(false);
    try {
      await api.post(`/messaging/channels/${channelId}/messages`, { text: t, mentions: ids });
      await load(true);
      scrollToEnd();
      onActivity?.();
    } catch { /* ignore */ }
  };

  const react = async (mid, emoji) => {
    setPickerFor(null);
    try { await api.post(`/messaging/channels/${channelId}/messages/${mid}/react`, { emoji }); await load(false); }
    catch { /* ignore */ }
  };

  const matches = atOpen
    ? people.filter((p) => p.user_id !== me && (!atQuery || (p.name || p.email || "").toLowerCase().includes(atQuery.toLowerCase()))).slice(0, 6)
    : [];

  return (
    <div className="flex flex-col h-full" data-testid="chat-thread">
      <div ref={scrollRef} className="flex-1 overflow-auto px-3 py-3 space-y-2 bg-slate-50">
        {msgs.length === 0 && <div className="h-full flex items-center justify-center text-xs text-slate-400">No messages yet — say hello.</div>}
        {msgs.map((m, i) => {
          const mine = m.author_id === me;
          const seen = mine && i === lastMineIdx ? seenBy(m) : [];
          return (
            <div key={m.message_id} className={`flex flex-col ${mine ? "items-end" : "items-start"}`} data-testid={`msg-${m.message_id}`}>
              {!mine && <span className="text-[10px] text-slate-400 ml-1 mb-0.5">{m.author_name}</span>}
              <div className={`max-w-[78%] px-3 py-2 text-sm rounded-2xl ${mine ? "bg-[#003E87] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-800 rounded-bl-md"}`}>
                <span className="whitespace-pre-wrap break-words">{m.text}</span>
              </div>
              <span className="text-[10px] text-slate-400 mt-0.5 mx-1">{timeShort(m.created_at)}</span>
              {seen.length > 0 && (
                <span className="text-[10px] text-slate-400 mr-1" data-testid={`msg-seen-${m.message_id}`}>
                  Seen{seen.length <= 2 ? " by " + seen.map(firstName).filter(Boolean).join(", ") : ` by ${seen.length}`}
                </span>
              )}
              <div className={`flex items-center gap-1 mt-0.5 ${mine ? "flex-row-reverse" : ""}`}>
                {Object.entries(m.reactions || {}).map(([emoji, users]) => (
                  <button key={emoji} onClick={() => react(m.message_id, emoji)} data-testid={`reaction-${m.message_id}-${emoji}`}
                    className={`text-[11px] leading-none px-1.5 py-1 rounded-full border transition ${(users || []).includes(me) ? "bg-[#003E87]/10 border-[#003E87]/30 text-[#003E87]" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
                    {emoji} {(users || []).length}
                  </button>
                ))}
                <div className="relative">
                  <button onClick={() => setPickerFor(pickerFor === m.message_id ? null : m.message_id)} data-testid={`react-btn-${m.message_id}`}
                    className="text-slate-300 hover:text-slate-500 p-0.5 transition-colors" title="Add reaction"><SmilePlus className="w-3.5 h-3.5" /></button>
                  {pickerFor === m.message_id && (
                    <div className={`absolute bottom-full mb-1 flex gap-0.5 bg-white border border-slate-200 rounded-full shadow-lg px-1.5 py-1 z-20 ${mine ? "right-0" : "left-0"}`} data-testid={`react-picker-${m.message_id}`}>
                      {REACT_EMOJIS.map((e) => (
                        <button key={e} onClick={() => react(m.message_id, e)} data-testid={`react-emoji-${m.message_id}-${e}`}
                          className="text-base leading-none hover:scale-125 transition-transform px-0.5">{e}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div className="relative border-t border-slate-200 bg-white p-2">
        {atOpen && matches.length > 0 && (
          <div className="absolute bottom-full left-2 mb-1 w-56 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-10" data-testid="chat-mention-menu">
            {matches.map((p) => (
              <button key={p.user_id} onClick={() => pickMention(p)} data-testid={`chat-mention-${p.user_id}`}
                className="w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 text-slate-700">{p.name || p.email}</button>
            ))}
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea ref={taRef} value={text} onChange={onChange}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            rows={1} placeholder="Message… use @ to mention" data-testid="chat-input"
            className="flex-1 resize-none max-h-32 text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-[#003E87]" />
          <Button onClick={send} disabled={!text.trim()} className="bg-[#003E87] hover:bg-[#00306a] h-9 px-3" data-testid="chat-send"><Send className="w-4 h-4" /></Button>
        </div>
      </div>
    </div>
  );
}
